import './index.less';

import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Tooltip } from '@bfe/moly';
import { HelpIcon, MailIcon } from '@bfe/moly-icons';
import { RTLSkipStart } from '@bybit/rtl-plugin';
import { RTLSkipEnd } from '@bybit/rtl-plugin';
import { Divider, message, Space } from 'antd';
import React, { lazy, Suspense, useState } from 'react';

import AccountBasicItem from '@/components/AccountBasicItem';
import { SecuritySettingModalNames } from '@/components/SecuritySettingModalManager';
import { useI18nContext } from '@/i18n/I18n';
import * as SecurityApi from '@/services/security';
import { useSecuritySettingModal } from '@/store/useSecuritySettingModal/useSecuritySettingModal';
import { useStore } from '@/store/useStore';
import { useUserStore } from '@/store/useUser';
import { $tracing } from '@/utils/tracing';

const EmailUnBindModal = lazy(() => import('./EmailUnBindModal'));

const EmailAuth = () => {
  const [$t] = useI18nContext();
  const [unBindVisible, setUnBindVisible] = useState(false);
  const {
    userProfile: { userInfo: user },
    toggleWholeEmail,
    wholeEmail,
    showWholeEmail,
  } = useUserStore();
  const { msgError } = useStore(); // TODO： 手机号显示时，邮箱解绑弹窗里也要展示完整手机号
  const { openSecuritySettingModal } = useSecuritySettingModal();

  const handleClick = () => {
    const trackCode = 15130;
    $tracing.addEvent({
      t: 'action',
      c: trackCode,
    });
    openSecuritySettingModal(SecuritySettingModalNames.Email);
  };

  const emailOperation = () => {
    SecurityApi.emailOperation({ type: 'unbind' })
      .then((res) => {
        if (res.result.is_operation) {
          setUnBindVisible(true);
        } else {
          const errorTextObj: { [key: string]: string } = {
            1: $t('UnbindEmailNotSetPwdTip'),
            2: $t('unbindEmailErrorTip'),
          };
          message.error(errorTextObj[`${res.result.reason}`]);
        }
      })
      .catch((error) => {
        msgError(error);
      });
  };

  const openUnbindModal = () => {
    if (!user.has_google2fa || !user.mobile_verified) {
      message.error($t('cannotUbindEmailTip'));
      return;
    }
    emailOperation();
  };

  const openChangeEmailModal = () => {
    if (!user.has_google2fa) {
      message.error($t('changeEmailErrorTip'));
      return;
    }

    openSecuritySettingModal(SecuritySettingModalNames.Email);
  };

  const getManagedBy = () => {
    const name = $t('managed by');
    if (/\{\{name\}\}/.test(name)) {
      return $t('managed by').replace(
        '{{name}}',
        user.member_tag && (user.member_tag as any).third_party_register,
      );
    } else {
      return `Managed By ${
        user.member_tag && (user.member_tag as any).third_party_register
      }`;
    }
  };

  return (
    <>
      <AccountBasicItem
        icon="email"
        molyIcon={<MailIcon />}
        text={$t('Email Authentication')}
        subText={
          <>
            {user.member_tag &&
              (user.member_tag as any).third_party_register && (
                <>
                  <span className="managedBy">{getManagedBy()}</span>
                  <Divider type="vertical" style={{ marginTop: '6px' }} />
                </>
              )}
            {$t(
              'For Login, withdraw, password retrieval, security settings change and API management verification, once set cannot be changed',
            )}
            {user.email_verified && (
              <>
                <span
                  onClick={openUnbindModal}
                  className="managedBy link_a"
                  style={{
                    marginLeft: '8px',
                  }}
                >
                  {$t('unBindEmailTip')}
                </span>
              </>
            )}
          </>
        }
        status={!!user.email_verified}
        statusNode={
          <>
            {user.email_verified ? (
              <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Space size={4} style={{ lineHeight: 1 }}>
                  {!showWholeEmail ? (
                    <>
                      <span>{user.vague_email_v2}</span>
                      <EyeInvisibleOutlined
                        // style={eyeIconStyle}
                        onClick={toggleWholeEmail}
                      />
                    </>
                  ) : (
                    <>
                      <span>{wholeEmail}</span>
                      <EyeOutlined onClick={toggleWholeEmail} />
                    </>
                  )}
                </Space>
                {wholeEmail?.endsWith('@privaterelay.appleid.com') && (
                  <div className="apple-private-email-tip">
                    <RTLSkipStart></RTLSkipStart>
                    <Tooltip
                      title={
                        <div>
                          {
                            $t('appleEmailHideMyEmailTip').split(
                              '@privaterelay.appleid.com',
                            )[0]
                          }
                          <span style={{ color: '#D6850D', fontWeight: '500' }}>
                            @privaterelay.appleid.com
                          </span>{' '}
                          {
                            $t('appleEmailHideMyEmailTip').split(
                              '@privaterelay.appleid.com',
                            )[1]
                          }
                        </div>
                      }
                      placement="top"
                    >
                      <HelpIcon
                        style={{
                          width: '14px',
                          height: '14px',
                          cursor: 'pointer',
                        }}
                      />
                    </Tooltip>
                    <RTLSkipEnd></RTLSkipEnd>
                  </div>
                )}
              </div>
            ) : (
              <span className="not-set-text">{$t('Not Set')}</span>
            )}
          </>
        }
        operation={
          <>
            {user.email_verified ? (
              <Button
                data-radius="small"
                variant="secondary"
                size="small"
                onClick={openChangeEmailModal}
              >
                {$t('changeEmailBtnText')}
              </Button>
            ) : (
              <Button
                data-radius="small"
                variant="primary"
                size="small"
                onClick={handleClick}
              >
                {$t('Setting')}
              </Button>
            )}
          </>
        }
      />

      <Suspense fallback={<></>}>
        {unBindVisible && (
          <EmailUnBindModal
            visible={unBindVisible}
            onClose={() => {
              setUnBindVisible(false);
            }}
          />
        )}
      </Suspense>
    </>
  );
};
export default EmailAuth;
